import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "../components/atoms/Typography";
import { Button } from "../components/atoms/Button";
import { TemplatesTable } from "../components/organisms/TemplatesTable";
import { CreateTemplateDrawer } from "../components/organisms/CreateTemplateDrawer";
import type { Template, TemplateFormData, TemplateStatus } from "../types/templates";
import {
  listTemplates,
  uploadTemplate,
  updateTemplateStatus,
  type TemplateSortBy
} from "../services/templatesService";
import { ArrowLeft } from "lucide-react";

export const TemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<TemplateSortBy>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listTemplates({ sort_by: sortBy, sort_dir: sortDir });
      setTemplates(data);
    } catch {
      setError("Unable to load templates. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [sortBy, sortDir]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const handleCreate = useCallback(
    async (formData: TemplateFormData) => {
      await uploadTemplate(formData);
      await loadTemplates();
      setIsDrawerOpen(false);
    },
    [loadTemplates]
  );

  const handleToggleStatus = useCallback(
    (id: string, newStatus: TemplateStatus) => {
      setTemplates((prev) =>
        prev.map((t) => (t.id === id ? { ...t, status: newStatus } : t))
      );

      void (async () => {
        try {
          const confirmedStatus = await updateTemplateStatus(id, newStatus);
          setTemplates((prev) =>
            prev.map((t) =>
              t.id === id ? { ...t, status: confirmedStatus } : t
            )
          );
        } catch {
          // revert on failure
          setTemplates((prev) =>
            prev.map((t) =>
              t.id === id
                ? { ...t, status: newStatus === "Active" ? "Inactive" : "Active" }
                : t
            )
          );
          // simple user feedback; can be replaced with toast
          // eslint-disable-next-line no-alert
          alert("Failed to update template status. Please try again.");
        }
      })();
    },
    []
  );

  const handleSort = useCallback((newSortBy: TemplateSortBy, newSortDir: "asc" | "desc") => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
  }, []);

  const handleDownload = useCallback((template: Template) => {
    const url = template.templateDownloadUrl;
    if (!url) {
      alert("Download is not available for this template. Refresh the list and try again.");
      return;
    }

    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = template.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  return (
    <section className="page-section">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/contracts-selection")}
            className="back-link"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
          </button>
          <div>
            <Typography as="h1" variant="h1">
              Templates
            </Typography>
            <p className="page-subtitle">
              Manage your document templates
            </p>
          </div>
        </div>
        <Button className="flex items-center gap-2" onClick={() => setIsDrawerOpen(true)}>
          Add Template
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-status-error bg-red-50 px-4 py-3 text-sm text-status-error">
          {error}
        </div>
      )}

      {isLoading ? (
        <Typography variant="muted">Loading templates…</Typography>
      ) : (
        <TemplatesTable
          templates={templates}
          onToggleStatus={handleToggleStatus}
          onDownload={handleDownload}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
        />
      )}

      <CreateTemplateDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onCreate={handleCreate}
      />
    </section>
  );
};
